/**
 * DraftPreview — render visual del borrador IA antes de aceptarlo.
 *
 * Antes (AI-1.d / 1.d.b): un `<pre>` mostraba `draft_text` crudo. Para
 * `rich_text` se veía OK porque markdown plano es legible, pero para
 * `structured_table`, `bullet_list`, `structured`, `key_value_grid`,
 * etc., el JSON crudo se veía feo (llaves, comillas, comas).
 *
 * Ahora el componente despacha por `sectionType` y renderiza:
 *   · rich_text       → markdown → HTML via `marked.parse` (clase `prose-hiva`)
 *   · plain_text      → texto plano preservando líneas
 *   · bullet_list     → `<ul>` con cada item del JSON array
 *   · numbered_list   → `<ol>` análogo
 *   · checklist       → checkboxes deshabilitados (visual)
 *   · key_value_grid  → grid 2-cols label/value
 *   · structured_table→ tabla HTML inferiendo columns del primer row
 *   · structured      → array→tabla / object→cards de pares clave-valor
 *   · mermaid_*       → bloque code mono (render real está fuera de scope
 *                       del preview; basta con mostrar el código)
 *   · code_block      → bloque code con lenguaje
 *   · fallback        → `<pre>` (mismo que antes, para tipos nuevos)
 *
 * Tolerante a JSON malformado: si el parseo falla, cae al `<pre>` crudo
 * para que el user al menos vea el draft y pueda regenerar.
 */

import { useMemo } from 'react'
import { marked } from 'marked'

interface DraftPreviewProps {
  draftText: string
  sectionType: string
}

export function DraftPreview({ draftText, sectionType }: DraftPreviewProps) {
  if (sectionType === 'rich_text') {
    return <RichTextPreview markdown={draftText} />
  }
  if (sectionType === 'plain_text') {
    return <PlainTextPreview text={draftText} />
  }
  if (sectionType === 'bullet_list') {
    return <ListPreview draftText={draftText} ordered={false} />
  }
  if (sectionType === 'numbered_list') {
    return <ListPreview draftText={draftText} ordered={true} />
  }
  if (sectionType === 'checklist') {
    return <ChecklistPreview draftText={draftText} />
  }
  if (sectionType === 'key_value_grid') {
    return <KeyValueGridPreview draftText={draftText} />
  }
  if (sectionType === 'structured_table') {
    return <StructuredTablePreview draftText={draftText} />
  }
  if (sectionType === 'structured') {
    return <StructuredPreview draftText={draftText} />
  }
  if (sectionType === 'code_block') {
    return <CodeBlockPreview draftText={draftText} />
  }
  if (sectionType.startsWith('mermaid')) {
    return <MermaidSourcePreview draftText={draftText} />
  }
  return <PreFallback draftText={draftText} />
}

// ─── Renderers ────────────────────────────────────────────────────────────

function RichTextPreview({ markdown }: { markdown: string }) {
  const html = useMemo(() => {
    try {
      return marked.parse(markdown.trim(), { async: false }) as string
    } catch {
      return ''
    }
  }, [markdown])
  if (!html) return <PreFallback draftText={markdown} />
  return (
    <div
      className="prose-hiva max-h-[40vh] overflow-y-auto rounded-md border border-line bg-canvas px-4 py-3 text-sm font-body text-ink"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function PlainTextPreview({ text }: { text: string }) {
  return (
    <div className="max-h-[40vh] overflow-y-auto whitespace-pre-wrap rounded-md border border-line bg-canvas px-4 py-3 text-sm font-body text-ink">
      {text}
    </div>
  )
}

function ListPreview({
  draftText,
  ordered,
}: {
  draftText: string
  ordered: boolean
}) {
  const items = useMemo(() => tryParseList(draftText), [draftText])
  if (!items) return <PreFallback draftText={draftText} />
  const ListTag = ordered ? 'ol' : 'ul'
  const listClass = ordered ? 'list-decimal' : 'list-disc'
  return (
    <div className="max-h-[40vh] overflow-y-auto rounded-md border border-line bg-canvas px-5 py-3 text-sm font-body text-ink">
      <ListTag className={`${listClass} space-y-1 pl-5`}>
        {items.map((it, idx) => (
          <li key={idx}>{renderItemValue(it)}</li>
        ))}
      </ListTag>
    </div>
  )
}

function ChecklistPreview({ draftText }: { draftText: string }) {
  const items = useMemo(() => tryParseChecklist(draftText), [draftText])
  if (!items) return <PreFallback draftText={draftText} />
  return (
    <div className="max-h-[40vh] overflow-y-auto rounded-md border border-line bg-canvas px-4 py-3 text-sm font-body text-ink">
      <ul className="space-y-1.5">
        {items.map((it, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={it.checked}
              disabled
              className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-brand"
              aria-hidden="true"
              readOnly
            />
            <span className={it.checked ? 'text-ink-subtle line-through' : ''}>
              {it.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function KeyValueGridPreview({ draftText }: { draftText: string }) {
  const pairs = useMemo(() => tryParseKeyValuePairs(draftText), [draftText])
  if (!pairs) return <PreFallback draftText={draftText} />
  return (
    <div className="max-h-[40vh] overflow-y-auto rounded-md border border-line bg-canvas px-4 py-3 text-sm font-body text-ink">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2">
        {pairs.map((p, idx) => (
          <div key={idx} className="contents">
            <dt className="font-mono text-xs uppercase tracking-wider text-ink-subtle">
              {p.key}
            </dt>
            <dd className="text-ink">{renderItemValue(p.value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function StructuredTablePreview({ draftText }: { draftText: string }) {
  const rows = useMemo(() => tryParseObjectArray(draftText), [draftText])
  if (!rows || rows.length === 0) return <PreFallback draftText={draftText} />
  const columns = inferColumns(rows)
  return (
    <div className="max-h-[40vh] overflow-auto rounded-md border border-line bg-canvas text-sm font-body text-ink">
      <table className="min-w-full border-collapse">
        <thead className="bg-surface-muted">
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                className="border-b border-line px-3 py-2 text-left font-mono text-xs uppercase tracking-wider text-ink-subtle"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b border-line/60 last:border-0">
              {columns.map((col) => (
                <td key={col} className="px-3 py-2 align-top">
                  {renderItemValue(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function StructuredPreview({ draftText }: { draftText: string }) {
  const parsed = useMemo(() => tryParseJson(draftText), [draftText])
  if (parsed === undefined) return <PreFallback draftText={draftText} />

  if (Array.isArray(parsed)) {
    const objectRows = parsed.filter(
      (r) => r && typeof r === 'object' && !Array.isArray(r),
    ) as Record<string, unknown>[]
    if (objectRows.length === parsed.length && objectRows.length > 0) {
      return (
        <StructuredTablePreview draftText={JSON.stringify(objectRows)} />
      )
    }
    return (
      <ListPreview draftText={JSON.stringify(parsed)} ordered={false} />
    )
  }

  if (parsed && typeof parsed === 'object') {
    const entries = Object.entries(parsed as Record<string, unknown>)
    return (
      <div className="max-h-[40vh] space-y-2 overflow-y-auto rounded-md border border-line bg-canvas px-4 py-3 text-sm font-body text-ink">
        {entries.map(([k, v]) => (
          <div
            key={k}
            className="rounded-sm border border-line/60 bg-surface-muted/40 px-3 py-2"
          >
            <p className="font-mono text-[11px] uppercase tracking-wider text-ink-subtle">
              {k}
            </p>
            <div className="mt-0.5 text-ink">{renderItemValue(v)}</div>
          </div>
        ))}
      </div>
    )
  }

  return <PreFallback draftText={draftText} />
}

function CodeBlockPreview({ draftText }: { draftText: string }) {
  const parsed = useMemo(() => tryParseJson(draftText), [draftText])
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const obj = parsed as Record<string, unknown>
    const code = typeof obj['code'] === 'string' ? (obj['code'] as string) : ''
    const language =
      typeof obj['language'] === 'string'
        ? (obj['language'] as string)
        : 'text'
    if (code) {
      return (
        <div className="max-h-[40vh] overflow-auto rounded-md border border-line bg-canvas">
          {language && (
            <div className="border-b border-line bg-surface-muted px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
              {language}
            </div>
          )}
          <pre className="overflow-x-auto px-4 py-3 font-mono text-xs text-ink">
            {code}
          </pre>
        </div>
      )
    }
  }
  return <PreFallback draftText={draftText} />
}

function MermaidSourcePreview({ draftText }: { draftText: string }) {
  return (
    <div className="max-h-[40vh] overflow-auto rounded-md border border-line bg-canvas">
      <div className="border-b border-line bg-surface-muted px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
        mermaid
      </div>
      <pre className="overflow-x-auto px-4 py-3 font-mono text-xs text-ink">
        {draftText}
      </pre>
    </div>
  )
}

function PreFallback({ draftText }: { draftText: string }) {
  return (
    <pre className="prose-hiva max-h-[40vh] overflow-y-auto whitespace-pre-wrap rounded-md border border-line bg-canvas px-4 py-3 text-sm font-body text-ink">
      {draftText}
    </pre>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/** Intenta parsear el draft como JSON. `undefined` si falla. */
function tryParseJson(text: string): unknown {
  const trimmed = text.trim()
  if (!trimmed) return undefined
  try {
    return JSON.parse(trimmed)
  } catch {
    // Algunos LLMs envuelven en ```json … ```. Limpiamos fence.
    const fenced = trimmed.replace(/^```(?:json)?\s*|\s*```$/g, '').trim()
    if (fenced !== trimmed) {
      try {
        return JSON.parse(fenced)
      } catch {
        return undefined
      }
    }
    return undefined
  }
}

/** Para listas: el agente puede devolver `string[]` o `{value: ...}[]`. */
function tryParseList(text: string): unknown[] | null {
  const parsed = tryParseJson(text)
  if (Array.isArray(parsed)) return parsed
  return null
}

/** Para checklist: `{text, checked}[]` (variaciones toleradas). */
function tryParseChecklist(
  text: string,
): { text: string; checked: boolean }[] | null {
  const parsed = tryParseJson(text)
  if (!Array.isArray(parsed)) return null
  const out: { text: string; checked: boolean }[] = []
  for (const it of parsed) {
    if (typeof it === 'string') {
      out.push({ text: it, checked: false })
      continue
    }
    if (it && typeof it === 'object' && !Array.isArray(it)) {
      const obj = it as Record<string, unknown>
      const textVal =
        typeof obj['text'] === 'string'
          ? obj['text']
          : typeof obj['label'] === 'string'
            ? obj['label']
            : ''
      const checkedVal = obj['checked'] === true || obj['done'] === true
      if (textVal) out.push({ text: textVal, checked: checkedVal })
    }
  }
  return out.length > 0 ? out : null
}

/** Para key_value_grid: `{key, value}[]`. */
function tryParseKeyValuePairs(
  text: string,
): { key: string; value: unknown }[] | null {
  const parsed = tryParseJson(text)
  if (!Array.isArray(parsed)) return null
  const out: { key: string; value: unknown }[] = []
  for (const it of parsed) {
    if (it && typeof it === 'object' && !Array.isArray(it)) {
      const obj = it as Record<string, unknown>
      const k =
        typeof obj['key'] === 'string'
          ? obj['key']
          : typeof obj['name'] === 'string'
            ? obj['name']
            : null
      if (!k) continue
      out.push({ key: k, value: obj['value'] })
    }
  }
  return out.length > 0 ? out : null
}

/** Para tabla/structured-array: cada item debe ser objeto plano. */
function tryParseObjectArray(text: string): Record<string, unknown>[] | null {
  const parsed = tryParseJson(text)
  if (!Array.isArray(parsed)) return null
  const out: Record<string, unknown>[] = []
  for (const it of parsed) {
    if (it && typeof it === 'object' && !Array.isArray(it)) {
      out.push(it as Record<string, unknown>)
    }
  }
  return out.length > 0 ? out : null
}

/** Une las keys de cada row preservando el orden de aparición del
 * primer row donde aparece cada key (estable visualmente). */
function inferColumns(rows: Record<string, unknown>[]): string[] {
  const seen = new Set<string>()
  const cols: string[] = []
  for (const row of rows) {
    for (const k of Object.keys(row)) {
      if (!seen.has(k)) {
        seen.add(k)
        cols.push(k)
      }
    }
  }
  return cols
}

/** Render seguro de un valor cualquiera dentro de una celda/item. */
function renderItemValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  // Object/array → JSON compacto (no muy común en el preview pero
  // mejor mostrar algo legible que un `[object Object]`).
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}
